/**
 * Keeping the caret in view in the composer's textarea.
 *
 * The composer sizes its own field (`resizeField` in ChatComposer), and it
 * measures by releasing the height to zero and reading `scrollHeight` back.
 * For that instant the element is not scrollable, and the browser drops
 * `scrollTop` to 0 whenever an overflowing element stops being scrollable — so
 * past the row cap every keystroke would snap the view to the top of the
 * message. `resizeField` puts the raw offset back so an unfocused field holds
 * its place; this file decides what the offset should be when the field has
 * focus, which is the case where the caret has a claim on it.
 *
 * Two things to restore, and they disagree. Ordinary typing: the browser had
 * already scrolled this keystroke's caret into view before the measurement, and
 * `onChange` captured that offset, so putting it back changes nothing. Text
 * that arrives without a keystroke — dictation, history recall, a slash
 * completion — never gave the browser that chance, so there is no settled
 * offset worth keeping and the end of the value is where the writer is looking.
 */

export interface CaretScrollInput {
  /** Scroll offset captured before the resize cycle wiped it. */
  savedScrollTop: number;
  /** Whether the caret sits at the very end of the value. */
  caretAtEnd: boolean;
  /** Full scrollable height of the textarea. */
  scrollHeight: number;
  /** Visible height of the textarea. */
  clientHeight: number;
}

/**
 * The scroll offset to restore after a resize cycle.
 *
 * A caret at the end wins over the saved offset: text arriving programmatically
 * — dictation, history recall, a slash completion — never gave the browser a
 * chance to scroll, so there is nothing worth restoring, and the end of the
 * value is where the writer is looking.
 */
export const restoredScrollTop = ({
  savedScrollTop,
  caretAtEnd,
  scrollHeight,
  clientHeight,
}: CaretScrollInput): number => {
  const maxScroll = Math.max(0, scrollHeight - clientHeight);
  if (caretAtEnd) return maxScroll;
  return Math.min(Math.max(savedScrollTop, 0), maxScroll);
};
